import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import * as sfx from './launchSound.js';
import * as music from './bootMusic.js';
import { useVoice, speak, hush, canSpeak } from '../../lib/speech.js';

/**
 * What is a Boot Loader? - an animated journey in ten scenes.
 *
 * Written from a video storyboard, and deliberately not made into one.
 * A film is watched at the film's pace; this is a lesson, and a learner
 * who has lost the thread needs to stop, go back a beat and read it
 * again. So it plays on its own like a film, and can be paused, stepped
 * and re-entered at any scene like a lesson.
 *
 * Every scene is built the same way: a dark stage holding one drawing,
 * and a `beat` counter that walks forward through it. Beats are what
 * make a scene arrive in pieces rather than all at once - a check
 * lands, then the next, then the caption changes - without anybody
 * having to write a timeline.
 *
 * All artwork is inline SVG, all motion is CSS keyframes. Nothing here
 * loads from anywhere: the production CSP allows no external media, and
 * a school connection should not have to carry a video.
 */

const STORE_KEY = 'cybercub.lab.boot-loader';
const BEAT_MS = 4200;

/* ==================================================================
   shared pieces
   ================================================================== */

/** The glow every bright thing on the stage borrows. */
function StageDefs() {
  return (
    <defs>
      <filter id="bl-glow" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="1.6" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

/**
 * A glowing packet of data crossing the stage.
 *
 * The start and end are handed to CSS as custom properties, so one
 * keyframe rule drives every packet in the film and the browser can
 * keep the whole thing on the compositor.
 */
function Packet({ from, to, delay = 0, tone = 'mint' }) {
  return (
    <circle
      className={`bl-packet is-${tone}`}
      r="1.5"
      style={{
        '--x1': `${from[0]}px`,
        '--y1': `${from[1]}px`,
        '--x2': `${to[0]}px`,
        '--y2': `${to[1]}px`,
        animationDelay: `${delay}s`,
      }}
    />
  );
}

/** A stream of packets along one route. */
function Stream({ from, to, count = 4, tone = 'mint', gap = 0.35 }) {
  return Array.from({ length: count }, (_, i) => (
    <Packet key={i} from={from} to={to} delay={i * gap} tone={tone} />
  ));
}

/**
 * A labelled component box.
 *
 * The geometry is forced through Number() because this does arithmetic
 * on it. Called as x="6" - which is how every native SVG attribute in
 * this file is written - "6" + 30 / 2 is the string "615", and the
 * label renders five thousand pixels off the side of the stage. It is
 * an easy mistake to make again, so it is fixed here rather than at
 * every call site.
 */
function Part({ x, y, w, h, label, sub, lit, tone = 'grape' }) {
  const [X, Y, W, H] = [x, y, w, h].map(Number);
  return (
    <g className={`bl-part is-${tone} ${lit ? 'is-lit' : ''}`}>
      <rect x={X} y={Y} width={W} height={H} rx="3" />
      <text x={X + W / 2} y={Y + H / 2 + (sub ? -1 : 1.5)} className="bl-part-label">
        {label}
      </text>
      {sub && (
        <text x={X + W / 2} y={Y + H / 2 + 5} className="bl-part-sub">{sub}</text>
      )}
    </g>
  );
}

/**
 * The Boot Loader.
 *
 * A small guide with a shield, and the one thing in the film that has
 * to be recognisable the moment it reappears - which is why it keeps
 * the same silhouette, colour and shield in every scene it turns up in,
 * however small it is drawn.
 */
function BootBot({ x, y, scale = 1, talking = false }) {
  return (
    <g className={`bl-bot ${talking ? 'is-talking' : ''}`} transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* shield */}
      <path className="bl-bot-shield" d="M-11,-4 L-11,3 Q-11,9 -6,12 Q-11,6 -6,4 L-6,-4 Z" />
      {/* body */}
      <rect className="bl-bot-body" x="-6" y="-6" width="12" height="12" rx="3.5" />
      {/* face */}
      <circle className="bl-bot-eye" cx="-2.2" cy="-1.4" r="1.25" />
      <circle className="bl-bot-eye" cx="2.2" cy="-1.4" r="1.25" />
      <path className="bl-bot-smile" d="M-2.4,2 Q0,4 2.4,2" />
      {/* antenna */}
      <line className="bl-bot-ant" x1="0" y1="-6" x2="0" y2="-9.5" />
      <circle className="bl-bot-spark" cx="0" cy="-10.4" r="1.2" />
    </g>
  );
}

/* ==================================================================
   the scenes
   ================================================================== */

/* ---- 1. the computer is sleeping ---- */
function ScenePower({ beat }) {
  const on = beat >= 2;
  return (
    <g className={on ? 'is-on' : 'is-off'}>
      {/* desk laptop */}
      <rect className="bl-shell" x="30" y="18" width="60" height="38" rx="3" />
      <rect className={`bl-screen ${on ? 'is-lit' : ''}`} x="34" y="22" width="52" height="30" rx="1.5" />
      {!on && <text x="60" y="40" className="bl-screen-text">Power OFF</text>}
      {on && <text x="60" y="40" className="bl-screen-text is-lit">…</text>}
      <path className="bl-shell" d="M22,56 H98 L104,64 H16 Z" />

      {/* power button */}
      <circle className={`bl-power ${beat >= 1 ? 'is-pressed' : ''}`} cx="92" cy="60" r="4" />
      <path className="bl-power-glyph" d="M92,57.4 V59.8 M89.9,58.2 A3,3 0 1 0 94.1,58.2" />

      {/* the surge, once pressed */}
      {beat >= 1 && (
        <>
          <Stream from={[92, 60]} to={[60, 37]} count={6} gap={0.18} />
          {Array.from({ length: 10 }, (_, i) => (
            <circle
              key={i}
              className="bl-spark"
              r="0.9"
              cx={92}
              cy={60}
              style={{ '--a': `${i * 36}deg`, animationDelay: `${i * 0.05}s` }}
            />
          ))}
        </>
      )}
    </g>
  );
}

/* ---- 2. the CPU wakes up ---- */
function SceneCpu({ beat }) {
  return (
    <g>
      <rect className="bl-board" x="10" y="10" width="100" height="54" rx="3" />
      {[18, 26, 34, 42, 50].map((y) => (
        <line key={y} className="bl-trace" x1="14" y1={y} x2="106" y2={y} />
      ))}

      <Part x="46" y="28" w="26" h="18" label="CPU" lit={beat >= 1} tone="coral" />
      <Part x="16" y="14" w="24" h="10" label="RAM" lit={beat >= 3} tone="sun" />
      <Part x="16" y="50" w="24" h="10" label="Storage" lit={beat >= 3} tone="sky" />
      <Part x="80" y="14" w="24" h="10" label="Board" lit={beat >= 2} tone="grape" />

      {beat >= 2 && <Stream from={[28, 24]} to={[59, 37]} count={4} tone="sun" />}
      {beat >= 2 && <Stream from={[28, 50]} to={[59, 37]} count={4} tone="sky" gap={0.4} />}

      {beat >= 4 && (
        <g className="bl-hunt">
          <circle cx="59" cy="37" r="16" className="bl-ring" />
          <circle cx="59" cy="37" r="22" className="bl-ring" style={{ animationDelay: '0.5s' }} />
        </g>
      )}
    </g>
  );
}

/* ---- 3. firmware appears ---- */
const CHECKS = ['CPU', 'RAM', 'Storage', 'Keyboard', 'Display'];

function SceneFirmware({ beat }) {
  return (
    <g>
      <Part x="8" y="48" w="104" h="12" label="Hardware" tone="sky" lit />
      <g className={beat >= 1 ? 'bl-fade-in' : 'bl-hidden'}>
        <Part x="8" y="30" w="104" h="12" label="Firmware — BIOS / UEFI" tone="sun" lit={beat >= 1} />
      </g>
      <Part x="8" y="12" w="104" h="12" label="CPU" tone="coral" lit={beat >= 2} />

      {beat >= 2 && <Stream from={[60, 24]} to={[60, 30]} count={3} tone="coral" gap={0.3} />}
      {beat >= 2 && <Stream from={[60, 42]} to={[60, 48]} count={3} tone="sun" gap={0.3} />}

      {/* the check list, landing one at a time */}
      {CHECKS.map((c, i) => (
        <g
          key={c}
          className={beat >= 3 + i ? 'bl-check is-in' : 'bl-check'}
          transform={`translate(${16 + i * 20} 68)`}
        >
          <circle r="4.2" className="bl-check-ring" />
          <path className="bl-check-tick" d="M-2,0 L-0.6,1.6 L2.2,-1.8" />
          <text y="9" className="bl-check-label">{c}</text>
        </g>
      ))}
    </g>
  );
}

/* ---- 4. meet the boot loader ---- */
function SceneMeet({ beat }) {
  return (
    <g>
      <Part x="6" y="28" w="30" h="16" label="Firmware" tone="sun" lit={beat >= 1} />
      <Part x="84" y="28" w="30" h="16" label="Operating System" tone="sky" lit={beat >= 3} />

      {beat >= 1 && <path className="bl-arrow" d="M36,36 H50" markerEnd="url(#bl-head)" />}
      {beat >= 3 && <path className="bl-arrow" d="M70,36 H84" markerEnd="url(#bl-head)" />}

      <g className={beat >= 2 ? 'bl-pop-in' : 'bl-hidden'}>
        <circle className="bl-bot-halo" cx="60" cy="36" r="17" />
        <BootBot x={60} y={36} scale={1.25} talking={beat >= 2} />
        <text x="60" y="60" className="bl-name">BOOT LOADER</text>
      </g>
    </g>
  );
}

/* ---- 5. the boot loader's job ---- */
const FILES = ['Boot files', 'Kernel', 'OS files'];

function SceneJob({ beat }) {
  return (
    <g>
      <Part x="6" y="8" w="34" h="12" label="Firmware" tone="sun" lit />
      <BootBot x={23} y={38} scale={1.05} talking={beat >= 1} />
      {beat >= 1 && <Stream from={[23, 20]} to={[23, 28]} count={2} tone="sun" gap={0.4} />}

      {/* storage, with its contents */}
      <rect className="bl-drive" x="48" y="8" width="34" height="46" rx="3" />
      <text x="65" y="16" className="bl-part-label">Storage</text>
      {FILES.map((f, i) => (
        <g key={f} className={beat >= 2 ? 'bl-fade-in' : 'bl-hidden'} style={{ animationDelay: `${i * 0.2}s` }}>
          <rect
            className={`bl-file ${f === 'Kernel' && beat >= 3 ? 'is-found' : ''}`}
            x="52" y={20 + i * 11} width="26" height="9" rx="2"
          />
          <text x="65" y={26.5 + i * 11} className="bl-file-label">{f}</text>
        </g>
      ))}

      {beat >= 2 && <g className="bl-scan"><rect x="48" y="8" width="34" height="46" rx="3" /></g>}

      {/* RAM, filling */}
      <Part x="92" y="24" w="24" h="16" label="RAM" tone="sun" lit={beat >= 4} />
      {beat >= 4 && <Stream from={[80, 31]} to={[92, 32]} count={5} tone="mint" gap={0.25} />}
    </g>
  );
}

/* ---- 6. the kernel takes over ---- */
function SceneKernel({ beat }) {
  return (
    <g>
      <g className={beat >= 2 ? 'bl-step-aside' : ''}>
        <BootBot x={20} y={36} scale={1} />
      </g>

      <g className={beat >= 1 ? 'bl-pop-in' : 'bl-hidden'}>
        <circle className="bl-kernel-halo" cx="60" cy="34" r="16" />
        <circle className="bl-kernel" cx="60" cy="34" r="11" />
        <text x="60" y="36" className="bl-name is-small">KERNEL</text>
      </g>

      {beat >= 1 && <path className="bl-arrow" d="M31,36 H46" markerEnd="url(#bl-head)" />}
      {beat >= 3 && <path className="bl-arrow" d="M74,34 H88" markerEnd="url(#bl-head)" />}

      {/* boot screen -> login -> desktop */}
      <g className={beat >= 3 ? 'bl-fade-in' : 'bl-hidden'}>
        <rect className="bl-screen is-lit" x="88" y="20" width="28" height="28" rx="2" />
        <text x="102" y="36" className="bl-screen-text is-lit">
          {beat >= 5 ? 'Desktop' : beat >= 4 ? 'Login' : 'Booting…'}
        </text>
      </g>
    </g>
  );
}

/* ---- 7. the school analogy ---- */
const SCHOOL = [
  { icon: '🚧', who: 'Firmware', is: 'the gate' },
  { icon: '🧭', who: 'Boot Loader', is: 'the guide' },
  { icon: '🏫', who: 'Kernel', is: 'the office' },
  { icon: '🎒', who: 'You', is: 'the student' },
];

function SceneSchool({ beat }) {
  return (
    <g>
      <path className="bl-school" d="M20,52 H100 V30 L60,14 L20,30 Z" />
      <rect className="bl-school-door" x="54" y="38" width="12" height="14" rx="1" />
      {SCHOOL.map((s, i) => (
        <g
          key={s.who}
          className={beat >= 1 + i ? 'bl-fade-in' : 'bl-hidden'}
          transform={`translate(${18 + i * 28} 64)`}
        >
          <text className="bl-emoji" y="0">{s.icon}</text>
          <text className="bl-school-who" y="8">{s.who}</text>
          <text className="bl-school-is" y="14">{s.is}</text>
        </g>
      ))}
    </g>
  );
}

/* ---- 8. secure boot ---- */
function SceneSecurity({ beat }) {
  const blocked = beat >= 4;
  return (
    <g>
      <text x="22" y="20" className="bl-emoji is-big">🕵️</text>
      {beat >= 1 && <text x="22" y="34" className="bl-warn">⚠ Boot Attack</text>}
      {beat >= 1 && <Stream from={[30, 24]} to={[52, 34]} count={3} tone="coral" gap={0.3} />}

      <BootBot x={60} y={34} scale={1.15} talking={beat >= 2} />

      {beat >= 2 && (
        <g className="bl-fade-in">
          <rect className="bl-sig" x="78" y="24" width="30" height="20" rx="3" />
          <text x="93" y="32" className="bl-sig-label">🔐 Secure Boot</text>
          <text x="93" y="39" className="bl-sig-sub">signature check</text>
        </g>
      )}

      {beat >= 3 && (
        <g className={`bl-verdict ${blocked ? 'is-bad' : 'is-good'}`}>
          <text x="60" y="60" className="bl-verdict-text">
            {blocked ? '❌  Modified — blocked' : '✅  Trusted — loaded'}
          </text>
        </g>
      )}
    </g>
  );
}

/* ---- 9. the whole journey ---- */
const PIPELINE = [
  'Power on',
  'CPU starts',
  'BIOS / UEFI',
  'Hardware check',
  'Boot loader',
  'Kernel into RAM',
  'Operating system',
  'Login / desktop',
];

function ScenePipeline({ beat }) {
  return (
    <g>
      {PIPELINE.map((p, i) => {
        const y = 8 + i * 8.2;
        const on = beat >= i;
        return (
          <g key={p} className={`bl-pipe ${on ? 'is-on' : ''}`}>
            <rect x="26" y={y} width="68" height="6.4" rx="3.2" />
            <text x="60" y={y + 4.6} className="bl-pipe-label">{p}</text>
            {i < PIPELINE.length - 1 && on && (
              <Packet from={[60, y + 6.4]} to={[60, y + 8.2]} tone="mint" />
            )}
          </g>
        );
      })}
    </g>
  );
}

/* ---- 10. the memory hook ---- */
const KEYWORDS = [
  { icon: '🚀', word: 'LOAD' },
  { icon: '🔍', word: 'SELECT' },
  { icon: '🔐', word: 'VERIFY' },
];

function SceneHook({ beat }) {
  return (
    <g>
      <text x="60" y="20" className="bl-ask">So… what does a boot loader do?</text>
      {beat >= 1 && (
        <text x="60" y="34" className="bl-answer bl-fade-in">
          It finds the operating system, loads the kernel into memory,
        </text>
      )}
      {beat >= 1 && (
        <text x="60" y="41" className="bl-answer bl-fade-in">
          and hands control over to it.
        </text>
      )}
      {KEYWORDS.map((k, i) => (
        <g
          key={k.word}
          className={beat >= 2 + i ? 'bl-pop-in' : 'bl-hidden'}
          transform={`translate(${26 + i * 34} 56)`}
        >
          <circle r="9" className="bl-key-ring" />
          <text className="bl-emoji" y="-1">{k.icon}</text>
          <text y="16" className="bl-key-word">{k.word}</text>
        </g>
      ))}
      {beat >= 5 && (
        <text x="60" y="80" className="bl-outro bl-fade-in">
          A lot happens before you see the desktop.
        </text>
      )}
    </g>
  );
}

/* ==================================================================
   the film
   ================================================================== */

/*
 * The script.
 *
 * Written to be spoken, not read: a hook, then a question, then the
 * answer, then what it means. Short sentences, because a long one read
 * aloud loses people halfway. The ellipses are there for the narrator
 * rather than the page - speech synthesis pauses on them, and the pause
 * before an answer is most of what makes anybody want it.
 *
 * Each beat carries its own cue, so a sound lands on the thing it is
 * about rather than at the top of a scene. Scenes carry a mood, which
 * is all the music bed is ever told.
 */
const SCENES = [
  {
    id: 'power', title: 'The computer is sleeping', mood: 'intro', Art: ScenePower,
    beats: [
      { say: 'Imagine you have just pressed the power button. Your screen is still black.', cue: 'click' },
      { say: 'Electricity floods in. Fans spin up. Lights come on.', cue: 'powerUp' },
      { say: 'And a few seconds later… your desktop is there. But wait. Who actually started it?' },
    ],
  },
  {
    id: 'cpu', title: 'The CPU wakes up', mood: 'calm', Art: SceneCpu,
    beats: [
      { say: 'Let us go inside and watch.', cue: 'cpuWake' },
      { say: 'The CPU wakes first. It is the part that carries out instructions.' },
      { say: 'Memory and storage get power too… but nothing has been put into them yet.', cue: 'dataFlow' },
      { say: 'And here is the catch. The CPU can only follow instructions. Right now, it has none.' },
      { say: 'So it looks in one fixed place, built into the machine, for the very first ones.', cue: 'dataFlow' },
    ],
  },
  {
    id: 'firmware', title: 'Firmware appears', mood: 'calm', Art: SceneFirmware,
    beats: [
      { say: 'What it finds there is firmware. Software kept on the machine itself, not on your disk.', cue: 'reveal' },
      { say: 'New computers call it U E F I. Older ones called it BIOS. Same first job.' },
      { say: 'Wake the hardware up… and check every piece of it.' },
      { say: 'Is the CPU there, and working?', cue: 'confirmBeep' },
      { say: 'Is there memory to work in?', cue: 'confirmBeep' },
      { say: 'Is there storage to read from?', cue: 'confirmBeep' },
      { say: 'Can you type? Can you see anything?', cue: 'confirmBeep' },
      { say: 'And remember. All of this happens before any operating system exists.' },
    ],
  },
  {
    id: 'meet', title: 'Meet the boot loader', mood: 'calm', Art: SceneMeet,
    beats: [
      { say: 'So the hardware is awake, and checked. But firmware does not run your operating system.' },
      { say: 'Something has to stand in the middle. Find the right system… and start it.' },
      { say: 'Meet the boot loader.', cue: 'reveal' },
      { say: 'Firmware. Boot loader. Operating system. Always in that order.' },
    ],
  },
  {
    id: 'job', title: 'What it actually does', mood: 'calm', Art: SceneJob,
    beats: [
      { say: 'Firmware starts it, then steps back.', cue: 'dataFlow' },
      { say: 'The boot loader goes looking through storage, where your operating system lives as files.' },
      { say: 'It is hunting for one thing. The kernel… the core of the whole operating system.' },
      { say: 'Found it. But the CPU cannot run it from there. Far too slow.', cue: 'loadRise' },
      { say: 'So the kernel is copied into memory. The same journey every program you open makes.', cue: 'loadRise' },
    ],
  },
  {
    id: 'kernel', title: 'The kernel takes over', mood: 'calm', Art: SceneKernel,
    beats: [
      { say: 'With the kernel in memory, the boot loader does the last thing it will ever do.' },
      { say: 'It hands over control… and stops. Its whole life lasted about two seconds.', cue: 'reveal' },
      { say: 'Now the kernel is in charge of the machine.' },
      { say: 'Drivers load. Services start. A boot screen appears.' },
      { say: 'Then a login screen, waiting for you.' },
      { say: 'And finally, the desktop. The part most people think of as the computer starting.', cue: 'confirmBeep' },
    ],
  },
  {
    id: 'school', title: 'Put simply', mood: 'calm', Art: SceneSchool,
    beats: [
      { say: 'If that is a lot to hold on to, think about arriving at school.' },
      { say: 'Firmware is the gate. It opens up, and checks everything is in order.' },
      { say: 'The boot loader is the guide who meets you there, and takes you where you need to be.' },
      { say: 'The kernel is the office that actually runs the place, once you are inside.' },
      { say: 'And you? You only ever see the classroom at the end of it.' },
    ],
  },
  {
    id: 'security', title: 'Why attackers care', mood: 'tense', Art: SceneSecurity,
    beats: [
      { say: 'Now. Here is the part that matters for security.', cue: 'warnLow' },
      { say: 'Everything you have just watched runs before the operating system. So, before any anti-virus.' },
      { say: 'Which means an attacker who replaces the boot loader owns that machine… from the very first moment.', cue: 'warnLow' },
      { say: 'That is exactly what Secure Boot is for. Each stage checks the next one is properly signed.', cue: 'lockOk' },
      { say: 'A tampered boot loader has the wrong signature. So it simply does not start.', cue: 'warnLow' },
    ],
  },
  {
    id: 'pipeline', title: 'The whole journey', mood: 'resolve', Art: ScenePipeline,
    beats: [
      { say: 'Power on.', cue: 'powerUp' },
      { say: 'The CPU starts, with no instructions of its own.', cue: 'cpuWake' },
      { say: 'Firmware takes the first turn.' },
      { say: 'The hardware is woken, and checked.', cue: 'confirmBeep' },
      { say: 'The boot loader is started.', cue: 'reveal' },
      { say: 'It finds the kernel, and copies it into memory.', cue: 'loadRise' },
      { say: 'The kernel takes control, and the operating system starts.' },
      { say: 'And there is your login screen. All of that. Every single time.', cue: 'confirmBeep' },
    ],
  },
  {
    id: 'hook', title: 'Remember this', mood: 'resolve', Art: SceneHook,
    beats: [
      { say: 'So… what does a boot loader actually do?' },
      { say: 'It finds your operating system, loads the kernel into memory, and hands over control.', cue: 'reveal' },
      { say: 'Load. It brings the kernel into memory.', cue: 'confirmBeep' },
      { say: 'Select. It chooses which system to start, when there is more than one.', cue: 'confirmBeep' },
      { say: 'Verify. It checks that what it is about to start can be trusted.', cue: 'lockOk' },
      { say: 'So next time your computer starts… remember how much happened first.' },
    ],
  },
];

const TOTAL_BEATS = SCENES.reduce((n, s) => n + s.beats.length, 0);

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

/**
 * The journey itself, without any page around it.
 *
 * Lives on its own at /lab/boot-loader and also as the opening section
 * of the Opening a Program lab, where it is the story that has to come
 * before the one about launching an application. One implementation,
 * two homes: they cannot drift apart.
 */
export function BootLoaderJourney({ onWatched }) {
  const saved = useMemo(load, []);
  const calm = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );

  const [si, setSi] = useState(0);          // scene
  const [beat, setBeat] = useState(0);
  const [playing, setPlaying] = useState(!calm);
  const [sound, setSound] = useState(false);
  const [narrating, setNarrating] = useState(false);
  const [watched, setWatched] = useState(Boolean(saved.watched));

  const voice = useVoice();
  const scene = SCENES[si];
  // Clamped, because a scene can be jumped to from a longer one before
  // the beat has been reset, and reading past the end takes the whole
  // journey down with it.
  const line = scene.beats[Math.min(beat, scene.beats.length - 1)];
  const lastScene = si === SCENES.length - 1;
  const finished = lastScene && beat >= scene.beats.length - 1 && !playing;

  useEffect(() => { save({ watched }); }, [watched]);
  useEffect(() => { if (watched) onWatched?.(); }, [watched, onWatched]);

  /* the clock: one beat, then the next, then the next scene */
  useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(() => {
      if (beat + 1 < scene.beats.length) { setBeat((b) => b + 1); return; }
      if (!lastScene) { setSi((n) => n + 1); setBeat(0); return; }
      setPlaying(false);
      setWatched(true);
    }, BEAT_MS);
    return () => clearTimeout(t);
  }, [playing, beat, si, scene.beats.length, lastScene]);

  /* The cue belonging to this beat, so it lands on the thing it is
     about rather than at the top of the scene. */
  useEffect(() => {
    if (sound && line.cue) sfx.play(line.cue);
  }, [si, beat, sound, line.cue]);

  /* The bed follows the scene's mood, and glides rather than cuts. */
  useEffect(() => {
    if (sound) music.setMood(scene.mood);
  }, [sound, scene.mood]);

  /* The narrator reads this beat, and the bed gets out of the way
     while it does. */
  useEffect(() => {
    if (!narrating) return undefined;
    speak(line.say, voice, {
      onStart: () => music.duck(true),
      onEnd: () => music.duck(false),
    });
    return () => { hush(); music.duck(false); };
  }, [si, beat, narrating, voice, line.say]);

  /* Never leave a voice talking to an empty room. */
  useEffect(() => () => {
    hush();
    music.stop();
  }, []);

  const goScene = (n) => { setSi(n); setBeat(0); };

  const step = (d) => {
    setPlaying(false);
    const next = beat + d;
    if (next >= 0 && next < scene.beats.length) { setBeat(next); return; }
    if (d > 0 && !lastScene) { setSi(si + 1); setBeat(0); return; }
    if (d < 0 && si > 0) { setSi(si - 1); setBeat(SCENES[si - 1].beats.length - 1); }
  };

  const toggleSound = () => setSound((on) => {
    if (on) { music.stop(); sfx.sleep(); return false; }
    const ok = sfx.wake();          // must happen inside the click
    if (ok) {
      music.start(scene.mood);
      if (line.cue) sfx.play(line.cue);
    }
    return ok;
  });

  const toggleNarration = () => setNarrating((on) => {
    if (on) { hush(); music.duck(false); return false; }
    if (!canSpeak()) return false;
    return true;
  });

  const done = SCENES.slice(0, si).reduce((n, s) => n + s.beats.length, 0) + beat + 1;
  const Art = scene.Art;

  return (
    <div className="bl">
      {/* ---------- the stage ---------- */}
      <div className="bl-stage">
        <svg viewBox="0 0 120 88" className="bl-canvas" aria-hidden="true">
          <StageDefs />
          <marker id="bl-head" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
            <path d="M0,0 L5,2.5 L0,5 Z" className="bl-arrow-head" />
          </marker>
          <g className="bl-grid">
            {Array.from({ length: 13 }, (_, i) => <line key={`v${i}`} x1={i * 10} y1="0" x2={i * 10} y2="88" />)}
            {Array.from({ length: 10 }, (_, i) => <line key={`h${i}`} x1="0" y1={i * 10} x2="120" y2={i * 10} />)}
          </g>
          {/* keyed on the scene so every entrance animation replays */}
          <g key={scene.id}><Art beat={beat} /></g>
        </svg>

        <div className="bl-caption">
          <span className="bl-caption-scene">
            Scene {si + 1} of {SCENES.length} · {scene.title}
          </span>
          <p key={`${scene.id}-${beat}`}>{line.say}</p>
        </div>
      </div>

      {/* ---------- controls ---------- */}
      <div className="bl-bar">
        <span className="bl-progress"><span style={{ width: `${(done / TOTAL_BEATS) * 100}%` }} /></span>
      </div>

      <div className="launch-controls bl-controls">
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            if (finished) { goScene(0); setPlaying(true); return; }
            setPlaying((p) => !p);
          }}
        >
          {finished ? '↻ Watch again' : playing ? '❚❚ Pause' : '▶ Play'}
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => step(-1)} disabled={si === 0 && beat === 0}>
          ← Back
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => step(1)}
          disabled={lastScene && beat === scene.beats.length - 1}
        >
          Next →
        </button>
        <button type="button" className={`btn btn-sm btn-ghost ${narrating ? 'is-on' : ''}`} onClick={toggleNarration} aria-pressed={narrating}>
          {narrating ? '🎙 Narration on' : '🎙 Narration off'}
        </button>
        <button type="button" className={`btn btn-sm btn-ghost ${sound ? 'is-on' : ''}`} onClick={toggleSound} aria-pressed={sound}>
          {sound ? '🔊 Sound & music on' : '🔇 Sound & music off'}
        </button>
      </div>

      {/* ---------- jump to any scene ---------- */}
      <ol className="bl-scenes">
        {SCENES.map((s, n) => (
          <li key={s.id}>
            <button
              type="button"
              className={`bl-scene ${n === si ? 'is-now' : ''} ${n < si ? 'is-done' : ''}`}
              onClick={() => { setPlaying(false); goScene(n); }}
              aria-current={n === si ? 'step' : undefined}
            >
              <span className="bl-scene-n">{n + 1}</span>
              {s.title}
            </button>
          </li>
        ))}
      </ol>

      {watched && (
        <div className="lab-done">
          <div>
            <strong>That is the whole story.</strong>
            <p style={{ margin: '0.3rem 0 0' }}>
              Power, firmware, boot loader, kernel, operating system — and the one in the middle is
              the one almost nobody has heard of.
            </p>
          </div>
          <Link to="/learn/lesson/cf1-inside-a-computer" className="btn btn-sm">← Back to the lesson</Link>
        </div>
      )}
    </div>
  );
}

/** The journey on its own page, reached from the lesson. */
export default function BootLoaderLab() {
  useEffect(() => { document.title = 'What is a Boot Loader? · CyberCub'; }, []);

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>What is a Boot Loader?</strong>
            <span className="lab-sub">Ethical Hacker · Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf1-inside-a-computer" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <BootLoaderJourney />
    </div>
  );
}
